import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Grid,
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Typography,
  Collapse,
  ButtonBase
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LabeledTextField from "../shared/ui/HaklaiLabeledTextField";
import CustomLink from "../../../layout/CustomLink";
import HelpDialog from "../../../layout/HelpDialog";
import haklaiHelpTexts from "../shared/haklaiHelpTexts";
import HasRabinovichDialog from "../../../shared/dialogs/HasRabinovichDialog";
import HasTzinunOnPtorDialog from "./components/HaklaiHasTzinunOnPtorDialog";
import {
  MAX_MONEY_AMOUNT,
  getHaklaiCurrencyOptions,
  RABINOVICH_WINDOW_START_EXCLUSIVE,
  RABINOVICH_WINDOW_END_EXCLUSIVE
} from "../shared/haklaiConstants";
import useHaklaiPreCalcStyles, {
  PRECALC_LABEL_WIDTHS,
  PRECALC_INPUT_WIDTHS
} from "./haklaiPreCalcStyles";
import {
  getHaklaiAcquisitionDetailsConfig,
  getHaklaiAcquisitionDisplayDate,
  getHaklaiPtorOptions
} from "./logic/haklaiPreCalcHelpers";
import {
  getHaklaiSelectedDateRateMessage,
  getHaklaiShekelCalculationMessage,
  HAKLAI_PRE_CALC_ERRORS,
  HAKLAI_PRE_CALC_LABELS,
  HAKLAI_PRE_CALC_MESSAGES
} from "./haklaiPreCalcTexts";
import moment from "moment";

/**
 * Owns the opening Haklai input section.
 *
 * Responsibilities:
 * - collects sale, acquisition, parcel, and exemption inputs
 * - reveals acquisition-specific fields based on the selected flow
 * - coordinates local field scrolling and prerequisite validation display
 *
 * Pure pre-calc date and option rules belong in `logic/`, while dialog UI
 * should stay under `components/`.
 */

// Reserve the FormHelperText slot beneath inputs even when no error is
// present, so validation messages don't grow the layout when they appear
// (and collapse it again when they clear). A non-breaking space keeps the
// slot at one caption-line tall regardless of CSS whitespace collapsing.
// Pairs with the label-side marginBottom toggle in LabeledTextField — now
// that helperText is always truthy, that toggle becomes a constant 22px
// instead of flipping on every error, which eliminates shifts on both
// sides of the Grid simultaneously.
const HELPER_TEXT_PLACEHOLDER = "\u00A0";
const PRE_CALC_SECTION_TRANSITION_MS = 180;
const WAY_OF_GETTING_PROPERTY_OPTIONS = Object.freeze([
  { value: "purchase", label: HAKLAI_PRE_CALC_LABELS.purchase },
  { value: "inheritance", label: HAKLAI_PRE_CALC_LABELS.inheritance },
  { value: "gift", label: HAKLAI_PRE_CALC_LABELS.gift }
]);
const ZABAN_OPTIONS = Object.freeze([
  { value: true, label: HAKLAI_PRE_CALC_LABELS.yes },
  { value: false, label: HAKLAI_PRE_CALC_LABELS.no }
]);
const BASIC_INFO_REVEAL_FIELDS = new Set(["soldPropertyDate", "totalPropertyCost"]);
const ACQUISITION_REVEAL_FIELDS = new Set([
  "wayOfGettingProperty",
  "deduDate",
  "testatorPassingDate",
  "testatorBoughtPropertyDate",
  "amount"
]);

const afterNextPaint = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};

const scrollToNamedField = (rootElement, fieldName) => {
  if (!rootElement || !fieldName) return false;
  const fieldElement = rootElement.querySelector(`[name="${fieldName}"]`);
  if (!fieldElement) return false;
  const scrollTarget =
    fieldElement.closest(".MuiFormControl-root")
    || fieldElement.closest(".MuiGrid-root")
    || fieldElement;

  scrollTarget.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });

  if (typeof fieldElement.focus === "function") {
    try {
      fieldElement.focus({ preventScroll: true });
    } catch (error) {
      fieldElement.focus();
    }
  }

  return true;
};

const formatHaklaiShekelAmount = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "";
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0
  }).format(numericValue);
};

const formatHaklaiUsdRate = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "";
  return new Intl.NumberFormat("he-IL", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  }).format(numericValue);
};

// Blur the clicked link *and* the currently-focused element before opening a
// dialog. Needed because dialogs returning focus to a previously-focused
// input would re-open the keyboard / re-trigger blur-validation on every open.
const blurFocusedElement = (event) => {
  const currentTarget = event?.currentTarget;
  if (currentTarget && typeof currentTarget.blur === "function") {
    currentTarget.blur();
  }

  const activeElement = typeof document !== "undefined" ? document.activeElement : null;
  if (activeElement && activeElement !== currentTarget && typeof activeElement.blur === "function") {
    activeElement.blur();
  }
};

const BasicInfoField = ({
  classes,
  label,
  helpDialog,
  labelWidth,
  alignLabelToInput = false,
  fieldClassName = "",
  belowMainRow = null,
  children
}) => {
  const mainRow = (
    <Box className={classes.basicInfoField}>
      <Box
        className={classes.basicInfoFieldHeader}
        style={{
          ...(labelWidth ? { minWidth: labelWidth } : {}),
          ...(alignLabelToInput ? { marginBottom: 22 } : {})
        }}
      >
        <Typography variant="subtitle1" className={classes.basicInfoFieldLabel}>
          {label}
        </Typography>
        {helpDialog ? <HelpDialog helpDialog={helpDialog} label={label} /> : null}
      </Box>
      <Box className={classes.basicInfoFieldControl}>{children}</Box>
    </Box>
  );

  return (
    <Grid item xs={12} sm="auto" className={fieldClassName}>
      {belowMainRow ? (
        <Box className={classes.basicInfoFieldStack}>
          {mainRow}
          <Box
            className={classes.basicInfoFieldBelowMainRow}
            style={{
              ...(labelWidth != null ? { marginInlineStart: labelWidth + 6 } : {})
            }}
          >
            {belowMainRow}
          </Box>
        </Box>
      ) : (
        mainRow
      )}
    </Grid>
  );
};

const InlineQuestionField = ({ classes, label, helpDialog, hint, errorText, children }) => {
  // The feedback caption (hint or error) is rendered unconditionally so the
  // vertical space it occupies is reserved from first paint. Without this,
  // the hint-to-error transition (or the hint-disappears-on-first-selection
  // moment) would collapse the caption line and shift every block below
  // upward. Error takes precedence over hint; when neither is present, a
  // non-breaking space keeps the line alive invisibly.
  const feedbackMessage = errorText || hint || HELPER_TEXT_PLACEHOLDER;
  const feedbackClass = errorText ? classes.inlineQuestionError : classes.inlineQuestionHint;
  return (
    <FormControl component="fieldset">
      <Box className={classes.inlineQuestionField}>
        <Typography variant="subtitle1" className={classes.inlineQuestionLabel}>
          {label}
        </Typography>
        {helpDialog ? <HelpDialog helpDialog={helpDialog} label={label} /> : null}
        {children}
      </Box>
      <Typography variant="caption" className={feedbackClass}>
        {feedbackMessage}
      </Typography>
    </FormControl>
  );
};

const HaklaiPreCalcSection = React.memo(forwardRef((props, ref) => {
  const classes = useHaklaiPreCalcStyles();
  const sectionRootRef = useRef(null);
  const [hasRabinovichDialogOpen, setHasRabinovichDialogOpen] = useState(false);
  const [tzinunDialogOpen, setTzinunDialogOpen] = useState(false);
  const handleAcquisitionDetailsOnChange = props.handleAcquisitionDetailsOnChange;
  const acquisitionAmount = Number(props.acquisitionDetails?.amount);
  const acquisitionCurrency = props.acquisitionDetails?.deduCurrency || "newShekel";
  const hasAcquisitionAmount = Number.isFinite(acquisitionAmount) && acquisitionAmount > 0;
  const acquisitionCostInShekelLabel = formatHaklaiShekelAmount(props.acquisitionCostInShekel);
  const usdRateLabel = formatHaklaiUsdRate(props.usdRate);
  const acquisitionContext = useMemo(() => ({
    wayOfGettingProperty: props.wayOfGettingProperty,
    acquisitionDetails: props.acquisitionDetails || {}
  }), [props.acquisitionDetails, props.wayOfGettingProperty]);
  const acquisitionDisplayDate = useMemo(
    () => getHaklaiAcquisitionDisplayDate(acquisitionContext),
    [acquisitionContext]
  );
  const acquisitionDetailsConfig = useMemo(
    () => (
      props.wayOfGettingProperty
        ? getHaklaiAcquisitionDetailsConfig(acquisitionContext)
        : null
    ),
    [acquisitionContext, props.wayOfGettingProperty]
  );
  const acquisitionCurrencyOptions = useMemo(
    () => getHaklaiCurrencyOptions(acquisitionDisplayDate),
    [acquisitionDisplayDate]
  );
  const showUsdRateWarning = (
    acquisitionCurrency === "usd"
    && hasAcquisitionAmount
    && acquisitionDisplayDate
    && !props.usdRateLoading
    && !usdRateLabel
  );
  const acquisitionConversionSummary = (() => {
    if (!hasAcquisitionAmount) return "";
    if (acquisitionCurrency === "newShekel") return "";
    if (acquisitionCurrency === "usd") {
      if (showUsdRateWarning) return "";
      if (!acquisitionDisplayDate) return "";
      if (props.usdRateLoading) return HAKLAI_PRE_CALC_MESSAGES.usdRateLoading;
      return getHaklaiSelectedDateRateMessage(acquisitionCostInShekelLabel, usdRateLabel);
    }
    if (!acquisitionCostInShekelLabel) return "";
    return getHaklaiShekelCalculationMessage(acquisitionCostInShekelLabel);
  })();
  // The Tzinun (cooling-period) question is only meaningful when the single-
  // apartment ptor is actively selected on a "gift" acquisition. ptorType
  // and hasTzinunOnPtor are already kept in sync by the onChange handler
  // (toggling ptorType resets hasTzinunOnPtor to false), so `ptorType ===
  // "single"` alone is a complete and non-fragile trigger.
  const shouldShowTzinunQuestion =
    props.wayOfGettingProperty === "gift" && props.ptorType === "single";
  const fieldErrors = props.fieldErrors || {};
  const handlePrerequisiteFieldBlur = props.handlePrerequisiteFieldBlur || (() => {});
  const [basicInfoExpanded, setBasicInfoExpanded] = useState(true);
  const [acquisitionExpanded, setAcquisitionExpanded] = useState(true);

  const revealField = useCallback((fieldName) => {
    if (!fieldName) return false;
    if (BASIC_INFO_REVEAL_FIELDS.has(fieldName)) setBasicInfoExpanded(true);
    if (ACQUISITION_REVEAL_FIELDS.has(fieldName)) setAcquisitionExpanded(true);
    afterNextPaint(() => scrollToNamedField(sectionRootRef.current, fieldName));
    return true;
  }, []);

  useImperativeHandle(ref, () => ({
    revealField
  }), [revealField]);

  const ptorOpts = useMemo(
    () => getHaklaiPtorOptions(props.wayOfGettingProperty),
    [props.wayOfGettingProperty]
  );

  // Check if Rabinovich checkbox should be shown
  const shouldShowRabinovich = useMemo(() => {
    if (!props.wayOfGettingProperty) return false;
    let dateToCheck;
    if (props.wayOfGettingProperty === "purchase" || props.wayOfGettingProperty === "gift") {
      dateToCheck = props.acquisitionDetails?.deduDate;
    } else if (props.wayOfGettingProperty === "inheritance") {
      dateToCheck = props.acquisitionDetails?.testatorBoughtPropertyDate;
    }
    if (!dateToCheck) return false;
    const date = moment(dateToCheck);
    if (!date.isValid()) return false;
    return (
      date.isBefore(moment(RABINOVICH_WINDOW_END_EXCLUSIVE), "day") &&
      date.isAfter(moment(RABINOVICH_WINDOW_START_EXCLUSIVE), "day")
    );
  }, [
    props.acquisitionDetails?.deduDate,
    props.acquisitionDetails?.testatorBoughtPropertyDate,
    props.wayOfGettingProperty
  ]);

  const renderAcquisitionDivider = (key) => (
    <Grid
      key={key}
      item
      xs={12}
      md="auto"
      className={`${classes.stretchGridItem} ${classes.acquisitionFieldDividerItem}`}
    >
      <Box className={classes.fieldDivider} />
    </Grid>
  );

  const renderAmountCurrencyFields = (amountLabel, currencyOpts) => (
    // Top-alignment (not center-alignment) pairs the amount input with the
    // currency select at their visual tops. Because `amount` reserves a
    // helper-text slot unconditionally (≈62px tall) while `currency` has
    // no helper-text (≈40px tall), `alignItems="center"` would drift the
    // currency ~11px below the amount input. Reserving a placeholder
    // helper-text on the currency select would match heights but also
    // leaks its orange `background` into the helper-text area, because
    // LabeledTextField paints `background` on the TextField root (which
    // in MUI v4 wraps the FormHelperText) instead of on the Input alone.
    <Grid item xs={12} sm="auto">
      <Grid container direction="row" spacing={1} alignItems="flex-start" wrap="wrap">
        <Grid item xs={12} sm="auto">
          <LabeledTextField
            type="number"
            label={amountLabel}
            name="amount"
            textFieldWidth={PRECALC_INPUT_WIDTHS.money}
            max={MAX_MONEY_AMOUNT}
            onChange={props.handleAcquisitionDetailsOnChange}
            value={props.acquisitionDetails?.amount || ""}
            helpDialog={haklaiHelpTexts.purchaseAmountInOriginalCurrency}
            error={Boolean(fieldErrors.amount)}
            helperText={fieldErrors.amount || HELPER_TEXT_PLACEHOLDER}
            centerHelperText
            stackLabelOnXs
            belowInputText={
              fieldErrors.amount
                ? ""
                : acquisitionConversionSummary || (showUsdRateWarning ? HAKLAI_PRE_CALC_MESSAGES.usdRateMissing : "")
            }
            belowInputClassName={
              showUsdRateWarning
                ? classes.usdRateWarning
                : classes.acquisitionConversionSummary
            }
            onBlur={() => handlePrerequisiteFieldBlur("amount")}
          />
        </Grid>
        <Grid item xs={12} sm="auto">
          <LabeledTextField
            type="select"
            name="deduCurrency"
            textFieldWidth={PRECALC_INPUT_WIDTHS.currency}
            onChange={props.handleAcquisitionDetailsOnChange}
            value={props.acquisitionDetails?.deduCurrency || "newShekel"}
            options={currencyOpts}
            background
            openMenuBelow
            selectTextColor="rgba(0, 0, 0, 0.87)"
          />
        </Grid>
      </Grid>
    </Grid>
  );

  const renderAcquisitionDetails = () => {
    if (!props.wayOfGettingProperty) return null;

    if (!acquisitionDetailsConfig) return null;
    const reserveConversionSlot = acquisitionDetailsConfig.showAmount === true;

    return (
      <Grid container direction="row" spacing={2} alignItems="flex-start" wrap="wrap">
        {acquisitionDetailsConfig.dateFields.map((field, index) => (
          <React.Fragment key={field.key}>
            {index > 0 ? renderAcquisitionDivider(`${field.key}-divider`) : null}
            <Grid item xs={12} sm="auto">
              <LabeledTextField
                type="date"
                label={field.label}
                name={field.name}
                onChange={props.handleAcquisitionDetailsOnChange}
                value={field.value}
                invalidInputMessage={HAKLAI_PRE_CALC_ERRORS.genericDateInvalid}
                validateInvalidInputOnBlur
                error={Boolean(fieldErrors[field.name])}
                helperText={fieldErrors[field.name] || HELPER_TEXT_PLACEHOLDER}
                centerHelperText
                stackLabelOnXs
                belowInputText={reserveConversionSlot ? HELPER_TEXT_PLACEHOLDER : undefined}
                belowInputClassName={classes.acquisitionConversionSummary}
                onBlur={() => handlePrerequisiteFieldBlur(field.name)}
              />
            </Grid>
          </React.Fragment>
        ))}
        {acquisitionDetailsConfig.showAmount ? (
          <>
            {acquisitionDetailsConfig.dateFields.length > 0 ? renderAcquisitionDivider("amount-divider") : null}
            {renderAmountCurrencyFields(acquisitionDetailsConfig.amountLabel, acquisitionCurrencyOptions)}
          </>
        ) : null}
      </Grid>
    );
  };

  const renderRabinovichSection = () => {
    if (!shouldShowRabinovich) return null;
    return (
      <Box className={classes.rabinovichSection}>
        <FormControl fullWidth component="fieldset">
          <Grid container direction="column" alignItems="flex-start">
            <Grid item>
              <FormControlLabel
                labelPlacement="end"
                control={
                  <Checkbox
                    checked={props.acquisitionDetails?.hasRabinovich || false}
                    value={!props.acquisitionDetails?.hasRabinovich}
                    onChange={props.handleAcquisitionDetailsOnChange}
                    name="hasRabinovich"
                    color="primary"
                  />
                }
                label={HAKLAI_PRE_CALC_LABELS.rabinovichEligibilityQuestion}
              />
            </Grid>
            <Grid item>
              <CustomLink
                label={HAKLAI_PRE_CALC_LABELS.checkEligibility}
                onClick={(event) => {
                  blurFocusedElement(event);
                  setHasRabinovichDialogOpen(true);
                }}
              />
            </Grid>
          </Grid>
        </FormControl>
      </Box>
    );
  };

  const handleRabinovichResults = useCallback((e) => {
    handleAcquisitionDetailsOnChange(e);
    setHasRabinovichDialogOpen(false);
  }, [handleAcquisitionDetailsOnChange]);

  const renderWayOfGettingPropertySection = () => (
    <InlineQuestionField
      classes={classes}
      label={HAKLAI_PRE_CALC_LABELS.wayOfGettingProperty}
      helpDialog={haklaiHelpTexts.wayOfGettingProperty}
      hint={
        !props.wayOfGettingProperty && !fieldErrors.wayOfGettingProperty
          ? HAKLAI_PRE_CALC_ERRORS.wayOfGettingPropertyRequired
          : null
      }
      errorText={fieldErrors.wayOfGettingProperty || ""}
    >
      <RadioGroup
        row
        name="wayOfGettingProperty"
        value={props.wayOfGettingProperty || ""}
        onChange={(e) => {
          props.handlePreCalcOnChange(e);
          handlePrerequisiteFieldBlur("wayOfGettingProperty");
        }}
      >
        {WAY_OF_GETTING_PROPERTY_OPTIONS.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            control={<Radio color="primary" size="small" />}
            label={opt.label}
            labelPlacement="end"
          />
        ))}
      </RadioGroup>
    </InlineQuestionField>
  );

  const renderPtorSection = () => {
    if (!props.wayOfGettingProperty) return null;

    return (
      <Box className={classes.sectionSubBlock}>
        <FormControl component="fieldset">
          <Box className={classes.ptorRow}>
            <Typography variant="subtitle1" className={classes.ptorTitle}>
              {HAKLAI_PRE_CALC_LABELS.hasPtor}
            </Typography>
            <Box className={classes.ptorInlineGroup}>
              {ptorOpts.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  classes={{ root: classes.ptorControlLabel }}
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      name="ptorType"
                      id={`ptorType_${opt.value}`}
                      checked={(props.ptorType || "none") === opt.value}
                      onChange={(e) => {
                        const nextValue = e.target.checked ? opt.value : "none";
                        props.handlePreCalcOnChange({ target: { name: "ptorType", value: nextValue } });
                        props.handlePreCalcOnChange({ target: { name: "hasTzinunOnPtor", value: false } });
                      }}
                    />
                  }
                  label={opt.label}
                  labelPlacement="end"
                />
              ))}
            </Box>
            {shouldShowTzinunQuestion ? <Box className={classes.ptorSpacer} /> : null}
            {shouldShowTzinunQuestion ? (
              <Box className={classes.tzinunInline}>
                <Typography variant="subtitle1" className={classes.inlineQuestionLabel}>
                  {HAKLAI_PRE_CALC_LABELS.hasTzinunOnPtor}
                </Typography>
                <RadioGroup
                  row
                  name="hasTzinunOnPtor"
                  value={Boolean(props.hasTzinunOnPtor).toString()}
                  onChange={(e) => {
                    const nextValue = e.target.value === "true";
                    props.handlePreCalcOnChange({ target: { name: "hasTzinunOnPtor", value: nextValue } });
                  }}
                >
                  <FormControlLabel
                    value={"true"}
                    classes={{ root: classes.ptorControlLabel }}
                    control={<Radio color="primary" size="small" />}
                    label={HAKLAI_PRE_CALC_LABELS.yes}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value={"false"}
                    classes={{ root: classes.ptorControlLabel }}
                    control={<Radio color="primary" size="small" />}
                    label={HAKLAI_PRE_CALC_LABELS.no}
                    labelPlacement="end"
                  />
                </RadioGroup>
                <Box className={classes.tzinunLinkWrap}>
                  <CustomLink
                    variant="caption"
                    label={HAKLAI_PRE_CALC_LABELS.checkTzinunEligibility}
                    onClick={(event) => {
                      blurFocusedElement(event);
                      setTzinunDialogOpen(true);
                    }}
                  />
                </Box>
              </Box>
            ) : null}
          </Box>
        </FormControl>
      </Box>
    );
  };

  const basicInfoFirstRowFields = [
    {
      key: "soldPropertyDate",
      label: HAKLAI_PRE_CALC_LABELS.soldDate,
      labelWidth: PRECALC_LABEL_WIDTHS.date,
      alignLabelToInput: true,
      control: (
        <LabeledTextField
          type="date"
          name="soldPropertyDate"
          onChange={props.handlePreCalcOnChange}
          value={props.soldPropertyDate || ""}
          invalidInputMessage={HAKLAI_PRE_CALC_ERRORS.soldDateInvalid}
          validateInvalidInputOnBlur
          error={Boolean(fieldErrors.soldPropertyDate)}
          helperText={fieldErrors.soldPropertyDate || HELPER_TEXT_PLACEHOLDER}
          centerHelperText
          onBlur={() => handlePrerequisiteFieldBlur("soldPropertyDate")}
        />
      )
    },
    {
      key: "totalPropertyCost",
      label: HAKLAI_PRE_CALC_LABELS.saleValue,
      labelWidth: PRECALC_LABEL_WIDTHS.money,
      alignLabelToInput: true,
      control: (
        <LabeledTextField
          type="number"
          name="totalPropertyCost"
          textFieldWidth={PRECALC_INPUT_WIDTHS.money}
          max={MAX_MONEY_AMOUNT}
          onChange={props.handlePreCalcOnChange}
          value={props.totalPropertyCost || ""}
          currency="₪"
          error={Boolean(fieldErrors.totalPropertyCost)}
          helperText={fieldErrors.totalPropertyCost || HELPER_TEXT_PLACEHOLDER}
          centerHelperText
          onBlur={() => handlePrerequisiteFieldBlur("totalPropertyCost")}
        />
      )
    }
  ];

  const hasZabanField = {
    key: "hasZaban",
    label: HAKLAI_PRE_CALC_LABELS.hasZaban,
    helpDialog: haklaiHelpTexts.totalPropertyCostWithoutZaban,
    labelWidth: PRECALC_LABEL_WIDTHS.zaban,
    alignLabelToInput: true,
    fieldClassName: classes.hasZabanBasicInfoField,
    control: (
      <Box className={classes.basicInfoRadioControl}>
        <RadioGroup
          row
          name="hasZaban"
          value={String(props.hasZaban !== undefined ? props.hasZaban : false)}
          onChange={props.handlePreCalcOnChange}
          className={classes.basicInfoRadioGroup}
        >
          {ZABAN_OPTIONS.map((opt) => (
            <FormControlLabel
              key={String(opt.value)}
              value={String(opt.value)}
              control={<Radio color="primary" size="small" />}
              label={opt.label}
              labelPlacement="end"
            />
          ))}
        </RadioGroup>
      </Box>
    )
  };

  const basicInfoFields = [...basicInfoFirstRowFields, hasZabanField];

  return (
    <Box ref={sectionRootRef}>
      <Grid container direction="column" spacing={3}>
        {/* Section 1: Basic Info */}
        <Grid item>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <ButtonBase
                className={classes.sectionHeaderButton}
                onClick={() => setBasicInfoExpanded((prev) => !prev)}
              >
                <Typography className={classes.sectionHeaderTitle}>
                  {HAKLAI_PRE_CALC_LABELS.basicInfoTitle}
                </Typography>
                <ExpandMoreIcon
                  className={[
                    classes.sectionToggleIcon,
                    basicInfoExpanded ? classes.sectionToggleIconExpanded : ""
                  ].filter(Boolean).join(" ")}
                />
              </ButtonBase>
            </Box>
            <Collapse in={basicInfoExpanded} timeout={PRE_CALC_SECTION_TRANSITION_MS} unmountOnExit={false}>
              <Box className={classes.sectionContent}>
                <Grid container direction="column" spacing={2}>
                  <Grid item className={classes.basicInfoFieldRow}>
                    <Grid container direction="row" spacing={2} alignItems="center" wrap="wrap">
                      {basicInfoFields.map((field) => (
                        <BasicInfoField
                          key={field.key}
                          classes={classes}
                          label={field.label}
                          helpDialog={field.helpDialog}
                          labelWidth={field.labelWidth}
                          alignLabelToInput={field.alignLabelToInput === true}
                          fieldClassName={field.fieldClassName}
                          belowMainRow={field.belowMainRow}
                        >
                          {field.control}
                        </BasicInfoField>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </Grid>

        {/* Section 2: Acquisition Details */}
        <Grid item>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <ButtonBase
                className={classes.sectionHeaderButton}
                onClick={() => setAcquisitionExpanded((prev) => !prev)}
              >
                <Typography className={classes.sectionHeaderTitle}>
                  {HAKLAI_PRE_CALC_LABELS.acquisitionDetailsTitle}
                </Typography>
                <ExpandMoreIcon
                  className={[
                    classes.sectionToggleIcon,
                    acquisitionExpanded ? classes.sectionToggleIconExpanded : ""
                  ].filter(Boolean).join(" ")}
                />
              </ButtonBase>
            </Box>
            <Collapse in={acquisitionExpanded} timeout={PRE_CALC_SECTION_TRANSITION_MS} unmountOnExit={false}>
              <Box
                className={`${classes.sectionContent} ${classes.sectionContentStack} ${classes.acquisitionSectionContent}`}
              >
                {/* Row: radio buttons | divider | acquisition details */}
                <Grid container direction="row" spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md="auto">
                    {renderWayOfGettingPropertySection()}
                  </Grid>
                  {props.wayOfGettingProperty && (
                    <>
                      <Grid item xs={12} md="auto" className={classes.stretchGridItem}>
                        <Box className={classes.acquisitionDivider} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Box className={classes.acquisitionDetailsStack}>
                          {renderAcquisitionDetails()}
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
                {renderPtorSection()}
                {renderRabinovichSection()}
              </Box>
            </Collapse>
          </Box>
        </Grid>
      </Grid>

      {/* HasRabinovichDialog */}
      <HasRabinovichDialog
        open={hasRabinovichDialogOpen}
        onClose={() => setHasRabinovichDialogOpen(false)}
        handleResults={handleRabinovichResults}
      />
      <HasTzinunOnPtorDialog
        soldPropertyDate={props.soldPropertyDate}
        open={tzinunDialogOpen}
        onClose={() => setTzinunDialogOpen(false)}
        helpTextsOverride={haklaiHelpTexts}
        requireSoldPropertyDate
        handleResults={(e) => props.handlePreCalcOnChange(e)}
      />
    </Box>
  );
}));

HaklaiPreCalcSection.displayName = "HaklaiPreCalcSection";

export default HaklaiPreCalcSection;
